import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Input, Spinner } from 'reactstrap';
import { getDefaultValue, validatePincodeByCountry, withComputedHidePinCode } from '../../pages/Form/components/PincodeField/helper';
import pincodeService from '../../pages/Form/components/PincodeField/PincodeService';
import PincodeField from '../../pages/Form/components/PincodeField/PincodeField';
import ButtonGroupNew from '../../pages/Form/forms/BusinessLiteForms/components/ButtonGroupNew';
import { addressDesc } from '../../pages/Form/forms/constants';
import { DialogContext } from '../../store/context/DialogContext';
import { useTranslation } from '../../store/context/TranslationContext';
import CountrySelectService from '../CSC/CountrySelect/CountrySelectService';
import { AddressAutocompleter } from '../InputField/AddressAutocompleter';
import { InputField } from '../InputField/InputField';
import { SelectField } from '../SelectField/SelectField';
import { getAddressString } from './CartService';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../InputField/testId';

const validationConfig = {
    street: {
        regex: /^[a-zA-Z0-9#]+[,. # a-zA-Z0-9/-]*$/,
        maxLength: 40,
        minLength: 1,
        error: 'Please enter a valid Street name'
    },
    buildingNumber: {
        regex: /^[a-zA-Z0-9#]+[,. # a-zA-Z0-9/-]*$/,
        maxLength: 40,
        minLength: 1,
        error: 'Please enter a valid Building and Number'
    },
    city: {
        regex: /^[a-zA-Z\s.,'&-]{1,50}$/,
        maxLength: 50,
        minLength: 1,
        error: 'Please enter a valid city name.'
    },
    state: {
        regex: /^[a-zA-Z\s.,'-]{1,50}$/,
        maxLength: 50,
        minLength: 1,
        error: 'Please enter a valid state'
    },
    pinCode: {
        regex: /^[a-zA-Z0-9\- ]{3,10}$/,
        maxLength: 10,
        error: 'Please enter a valid postal/zip code.'
    },
    country: {
        required: true,
        error: 'Please select a valid Country.'
    }
};

const addressLabels = {
    street: 'Street Name',
    buildingNumber: 'Building and Number',
    city: 'City',
    state: 'State / Province',
    pinCode: 'Zip Code',
    country: 'Country'
};

const validateAddress = (address) => {
    const errors = {};
    ["street", "buildingNumber", "city", "state", "pinCode", "country"].forEach(field => {
        const value = address?.[field] || '';
        const config = validationConfig[field];
        if (!config) return;
        // A country with a fixed postal code (e.g. HK -> 999077) hides the Zip field; never gate on
        // a value the user cannot see or edit - it is the country default.
        if (field === 'pinCode' && address?.hidePinCode) return;

        let error = '';
        if (config.required && !value?.trim()) {
            error = config.error;
        } else if (config.regex && !config.regex.test(value)) {
            error = config.error;
        } else if (value.length > config.maxLength) {
            error = config.error;
        } else if (config.minLength && value.length < config.minLength) {
            error = config.error;
        } else if (field === 'pinCode') {
            const country = address.country;
            if (!validatePincodeByCountry(country, value)) {
                error = config.error;
            }
        }

        if (error) {
            errors[field] = error;
        }
    });
    return errors;
};

const isAddressValid = (address) => {
    return Object.keys(validateAddress(address)).length === 0;
};

const getAddressObject = (formData) => {
    return {
        street: formData.street || '',
        buildingNumber: formData.buildingNumber || '',
        city: formData.city || '',
        state: formData.state || '',
        pinCode: formData.pinCode || '',
        country: formData.country || ''
    };
}

const IPlusAddress = ({
    data,
    onContinue,
    onBackClick,
    handleUpdateFormData,
    testId,
}) => {
    const { t } = useTranslation();
    const { showError } = useContext(DialogContext);

    const { COUNTRY_MASTER_KEYS } = CountrySelectService;

    const {
        SANCTION_COUNTRY: sanctionCountryKey,
        VISA_CARD: visaCardKey
    } = COUNTRY_MASTER_KEYS;

    const personalAddress = useMemo(() => data?.address || {}, [JSON.stringify(data?.address)]);;
    const initialMailingAddress = useMemo(() => data?.mailingAddress || {}, [JSON.stringify(data?.mailingAddress)]);;
    const isMailingValid = isAddressValid(initialMailingAddress);
    const areAddressesEqual = _.isEqual(getAddressObject(initialMailingAddress), getAddressObject(personalAddress));

    const [isSameAddress, setIsSameAddress] = useState(isMailingValid ? areAddressesEqual : true);
    const [formData, setFormData] = useState(isMailingValid ? (areAddressesEqual ? _.cloneDeep(personalAddress) : _.cloneDeep(initialMailingAddress)) : _.cloneDeep(personalAddress));
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const resolvedTestId = resolveAutomationTestId(testId);

    const clonePersonalAddress = () => {
        setFormData(prev => {
            if (!_.isEqual(prev, personalAddress)) {
                return _.cloneDeep(personalAddress);
            }
            return prev;
        });
    }

    useEffect(() => {
        if (isSameAddress && personalAddress) {
            clonePersonalAddress()
        }
    }, [isSameAddress, JSON.stringify(personalAddress)]);

    const handleToggle = () => {
        setIsSameAddress(prev => !prev);
    }

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateField = (field, value, address = formData) => {
        const config = validationConfig[field];
        if (!config) return '';

        // Hidden Zip field (country has a fixed default): don't validate what can't be edited.
        if (field === 'pinCode' && address?.hidePinCode) return '';

        if (config.required && !value?.trim()) return config.error;
        if (config.regex && !config.regex.test(value)) return config.error;
        if (value.length > config.maxLength) return config.error;
        if (config.minLength && value.length < config.minLength) return config.error;

        if (field === "pinCode" && !validatePincodeByCountry(address?.country, value)) {
            return config.error;
        }

        return '';
    };

    const validateForm = (address = formData) => {
        const errors = {};
        Object.keys(validationConfig).forEach(field => {
            const error = validateField(field, address[field], address);
            if (error) errors[field] = error;
        });
        return errors;
    };

    const handleNext = async () => {
        // Heal before validating/submitting: a country with a fixed postal code (e.g. HK 999077)
        // hides the Zip field, so an empty/stored-blank pinCode must be back-filled from the country
        // default - otherwise the hidden field dead-ends the user on "enter a valid postal/zip code"
        // and the backend rejects the empty value. fetch() is cached (no-op once loaded);
        // withComputedHidePinCode also corrects a stale hidePinCode flag from the current default.
        await pincodeService.fetch().catch(() => {});
        const healed = withComputedHidePinCode(formData);
        if (!_.isEqual(healed, formData)) setFormData(healed);

        const errors = validateForm(healed);
        if (Object.keys(errors).length > 0) {
            setHasError(true);
            const firstErrorField = Object.keys(errors)[0];
            showError(t(errors[firstErrorField]));
            return;
        }

        if (
            CountrySelectService?.cyclosCountryMasterMap?.[healed?.country]?.[visaCardKey] == "No"
            || CountrySelectService?.cyclosCountryMasterMap?.[healed?.country]?.[sanctionCountryKey] == "Yes"
        ) {
            const error = t(`We are currently not able to deliver a card to this shipping address. Please retry with another country's address.`);
            setHasError(true);
            showError(t(error));
            return
        }

        try {
            setLoading(true);
            const response = await handleUpdateFormData({
                ...data,
                mailingAddress: healed
            });
            if (response?.status == 200) {
                onContinue();
            }
        } catch (error) {
            showError(t('Failed to update address. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const renderAddressForm = () => {
        return <div className='row' data-testid={buildAutomationChildTestId(resolvedTestId, "form")}>
            <AddressAutocompleter
                id={buildAutomationChildTestId(resolvedTestId, "street")}
                placeholder={t(addressLabels.street)}
                value={formData.street}
                onChange={(v) => handleChange('street', v)}
                onSelect={(components) => {
                    handleChange('buildingNumber', components.buildingNumber || '');
                    handleChange('street', components.street || '');
                    handleChange('city', components.city || '');
                    handleChange('state', components.state || '');
                    // Only touch country/pinCode when the resolved place actually carries a
                    // country (components initializes it to ""); blanking a selected country
                    // made the country field flicker/toggle.
                    if (components.country) {
                        handleChange('pinCode', components.pinCode || '');
                        handleChange('country', components.country);
                        const defaultPincode = getDefaultValue(components.country);
                        handleChange('hidePinCode', !!defaultPincode);
                        if (defaultPincode) {
                            handleChange('pinCode', defaultPincode);
                        }
                    }
                }}
                error={t(validateField('street', formData.street))}
                showErr={hasError}
                desc={t(addressDesc.street)}
                isDisabled={loading}
                className="col-12"
            />

            {['buildingNumber'].map(field => (
                <InputField
                    data-testid={buildAutomationChildTestId(resolvedTestId, field)}
                    key={field}
                    placeholder={t(addressLabels[field])}
                    value={formData[field]}
                    onChange={(v) => handleChange(field, v)}
                    error={t(validateField(field, formData[field]))}
                    showErr={hasError}
                    type="text"
                    maxLength={validationConfig[field]?.maxLength || 40}
                    className={`col-12 `}
                    disabled={loading}
                    desc={t(addressDesc[field])}
                />
            ))}

            {['city', 'state'].map(field => (
                <InputField
                    data-testid={buildAutomationChildTestId(resolvedTestId, field)}
                    key={field}
                    placeholder={t(addressLabels[field])}
                    value={formData[field]}
                    onChange={(v) => handleChange(field, v)}
                    error={t(validateField(field, formData[field]))}
                    showErr={hasError}
                    type="text"
                    maxLength={validationConfig[field]?.maxLength || 40}
                    className={`col-12  col-sm-6`}
                    disabled={loading}
                    desc={t(addressDesc[field])}
                    regex={/^([a-zA-Z\s\-']*)$/}
                />
            ))}

            {!formData.hidePinCode && (
                <PincodeField
                    data-testid={buildAutomationChildTestId(resolvedTestId, "pin-code")}
                    key={"pinCode"}
                    countryCode={formData.country}
                    placeholder={t(addressLabels.pinCode)}
                    value={formData.pinCode}
                    onChange={(v) => handleChange('pinCode', v)}
                    error={t(validateField('pinCode', formData.pinCode))}
                    showErr={hasError}
                    maxLength={10}
                    className="col-12 col-sm-6 me-0 pe-0"
                    disabled={loading}
                />
            )}

            <SelectField
                data-testid={buildAutomationChildTestId(resolvedTestId, "country")}
                value={formData.country}
                error={t(validateField('country', formData.country))}
                showErr={hasError}
                data={CountrySelectService.cyclosCountryMaster}
                placeholder={t('Country')}
                label={t('Country')}
                className="col-12 col-sm-6 with-margin-top"
                isDisabled={loading}
                noLabel
                desc={addressDesc.country}
                onChange={async (v) => {
                    handleChange('country', v?.value);
                    // Ensure the postal-code config is loaded BEFORE reading the country's default
                    // pincode. The form prefetches it fire-and-forget, so on a fast country change the
                    // records could still be empty and getDefaultValue would wrongly return null - which
                    // is why e.g. Hong Kong (default 999077) was not auto-populating. fetch() is cached,
                    // so this is a no-op once records are loaded.
                    try {
                        await pincodeService.fetch();
                    } catch (e) {
                        // Non-blocking: fall through with whatever config is cached.
                    }
                    const defaultPincode = getDefaultValue(v?.value);
                    handleChange('hidePinCode', !!defaultPincode);
                    handleChange('pinCode', defaultPincode || '');
                }}
            />

            {formData.hidePinCode && (
                <div className="col-12 col-sm-6 me-0 pe-0" aria-hidden="true" />
            )}
        </div>
    }

    return (
        <div className={`d-flex flex-column justify-content-center align-items-center`} data-testid={resolvedTestId}>

            {loading && (
                <div className="fv_amount_due_full_screen_loader" data-testid={buildAutomationChildTestId(resolvedTestId, "loading")}>
                    <Spinner color="primary" />
                </div>
            )}

            <div className='fv_amount_due_header w-100' data-testid={buildAutomationChildTestId(resolvedTestId, "header")}>
                {t('Shipping Address')}
            </div>

            <div className="fv_amount_due_row mt-4 " data-testid={buildAutomationChildTestId(resolvedTestId, "personal-address-title")}>
                <span className='fw-600'>{t('Personal Address')}</span>
            </div>

            <div className="fv_amount_due_row mt-2 " data-testid={buildAutomationChildTestId(resolvedTestId, "personal-address")}>
                <span className=''>{t(getAddressString(data?.address, CountrySelectService?.cyclosCountryMasterMap))}</span>
            </div>

            <div className="fv_amount_due_row mt-4 " data-testid={buildAutomationChildTestId(resolvedTestId, "shipping-address-title")}>
                <span className='fw-600'>{t('Shipping Address')}</span>
            </div>

            <div className={`fv_amount_due_row mt-2 `} data-testid={buildAutomationChildTestId(resolvedTestId, "same-as-personal")}>
                <span className='d-flex gap-2 align-items-center'>
                    <Input
                        data-testid={buildAutomationChildTestId(resolvedTestId, "same-as-personal-input")}
                        type="checkbox"
                        checked={isSameAddress}
                        onChange={handleToggle}
                        disabled={loading}
                    />
                    {t("Same as above")}
                </span>
            </div>

            {!isSameAddress ? (
                <div className="mt-2" data-testid={buildAutomationChildTestId(resolvedTestId, "manual-address")}>
                    {renderAddressForm()}
                </div>
            ) : (
                <div className="fv_amount_due_row mt-2" data-testid={buildAutomationChildTestId(resolvedTestId, "same-address-preview")}>
                    <span className=''>{t(getAddressString(
                        {
                            'buildingNumber': formData?.buildingNumber || "",
                            'street': formData?.street || "",
                            'pinCode': formData?.pinCode || "",
                            'city': formData?.city || "",
                            'state': formData?.state || "",
                            'country': formData?.country || ""
                        },
                        CountrySelectService?.cyclosCountryMasterMap))}</span>
                </div>
            )}

            <div className='w-100 mt-5' data-testid={buildAutomationChildTestId(resolvedTestId, "footer")}>
                <ButtonGroupNew
                    testId={buildAutomationChildTestId(resolvedTestId, "navigation")}
                    primaryActionTestIdSuffix="continue"
                    onNextClick={handleNext}
                    onBackClick={onBackClick}
                    step={4}
                    isDisabled={loading}
                    nextText={t("Continue")}
                    asLabel={false}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default observer(IPlusAddress);
